import React from 'react'
import { AxiosError } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import config from '@/config/config.env'
import { routeTree } from '@/routeTree.gen'
import { NuqsAdapter } from 'nuqs/adapters/react'
// PostHog DISABLED — will re-enable after 1st deployment with new keys
// import { PostHogProvider } from '@posthog/react'
import { toast, Toaster } from 'sonner'
import { handleServerError } from '@/utils/handle-server-error'
import { AuthProvider } from '@/context/auth-context'
import JobUploadContext from '@/context/job-upload'
import { OrgProvider } from '@/context/org-context'
import ResDBContext from '@/context/resdb-upload'
import { SubscriptionProvider } from '@/context/subscription-context'
import { ThemeProvider } from '@/context/theme-context'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AppErrorBoundary } from '@/pages/errors/error-boundary'
import ConfirmDialogProvider from './conform-dialog'

declare module '@tanstack/react-router' {
  interface HistoryState {
    fromCommandMenu?: boolean
    title?: string
    description?: string
    from?:
    | string
    | {
      pathname: string
    }
    open_supademo?: true
    draftId?: string
    open_sync?: boolean
    access_token?: string
    access_denied?: boolean
    pathname?: string
    invites?: boolean
    templateId?: string | null
    openActivity?: boolean
    activityId?: string | null
    job?: {
      posting_success?: boolean
      title: string
    }
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Prevents request storms when switching tabs
      refetchOnReconnect: false,   // Prevents request storms on network flicker
      staleTime: 5 * 60 * 1000,       // 5-minute fresh window — no re-fetch on nav
      gcTime: 10 * 60 * 1000,          // Keep unused cache for 10 minutes
      refetchOnMount: false,           // Don't re-fetch if cache is still fresh
      retry: (failureCount, error) => {
        // Fail fast on Auth/Client errors
        if (error instanceof AxiosError && [401, 403, 404].includes(error.response?.status ?? 0)) {
          return false
        }
        if (import.meta.env.DEV) return failureCount < 1
        return failureCount < 3
      },
    },
    mutations: {
      onError: (error) => {
        handleServerError(error)
        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            // Content not modified
          }
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          toast.error('Session expired!')

          const redirect = `${router.history.location.href}`
          router.navigate({ to: '/login', search: { redirect } })
        }
        if (error.response?.status === 500) {
          toast.error('Internal Server Error')

          // toast({
          //   variant: 'destructive',
          //   content: 'Internal Server Error!',
          // })
          // router.navigate({ to: '/500' })
        }
        if (error.response?.status === 403) {
          router.navigate({ to: '/403', replace: true })
        }
      }
    },
  }),
})

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 30_000,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export default function Index({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider>
      <NuqsAdapter>
        {/* PostHog DISABLED — will re-enable after 1st deployment with new keys */}
          <ResDBContext>
            <ConfirmDialogProvider>
              <QueryClientProvider client={queryClient}>
                <JobUploadContext>
                  <TooltipProvider>
                    <AuthProvider>
                      <OrgProvider>
                        <SubscriptionProvider>
                          <AppErrorBoundary>
                            <Toaster richColors />
                            {children}
                          </AppErrorBoundary>
                        </SubscriptionProvider>
                      </OrgProvider>
                    </AuthProvider>
                  </TooltipProvider>
                </JobUploadContext>
              </QueryClientProvider>
            </ConfirmDialogProvider>
          </ResDBContext>
      </NuqsAdapter>
    </ThemeProvider>
  )
}
